/**
 * Agent-link preview spike — a one-page, no-backend preview of the three connected systems:
 * the derived agent character sheet, the 1->20 Link meter + unlock ladder, and casual daily
 * check-in tone (by Link level + bond pairing). Mounted at /spike/agent-link. NOT product
 * surface — it exists so the look can be approved before any Firestore/handler work.
 * Theme via ?theme=light|dark (so it can be screenshotted cleanly).
 */
import React, { useEffect } from 'react';
import AgentStatCard from './AgentStatCard';
import LinkMeter from './LinkMeter';
import CheckInBubble from './CheckInBubble';
import { SAMPLE_AGENTS, SAMPLE_CHECKINS } from './sampleData';

const AgentLinkSpike: React.FC = () => {
  const theme = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('theme') === 'dark' ? 'dark' : 'light';
  useEffect(() => {
    const root = document.documentElement;
    const had = root.classList.contains('dark');
    if (theme === 'dark') root.classList.add('dark'); else root.classList.remove('dark');
    return () => { if (had) root.classList.add('dark'); else root.classList.remove('dark'); };
  }, [theme]);

  return (
    <div data-testid="agent-link-spike" data-theme={theme} className="min-h-screen bg-white dark:bg-black p-6">
      <div className="max-w-5xl mx-auto space-y-10">
        <header>
          <h1 className="text-lg font-semibold text-secondary-900 dark:text-white">Agent character sheet · Link level · daily check-in — {theme}</h1>
          <p className="mt-1 text-sm text-secondary-400">Static preview. Numbers, unlocks and tone are illustrative — nothing is wired to the backend yet.</p>
        </header>

        {/* Character sheets */}
        <section>
          <h2 className="mb-3 text-[11px] font-medium uppercase tracking-wide text-secondary-400">Character sheet (derived attributes)</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {SAMPLE_AGENTS.map((a) => (
              <AgentStatCard key={a.id} name={a.name} nativeName={a.nativeName} persona={a.persona} icon={a.icon} stats={a.stats} />
            ))}
          </div>
        </section>

        {/* Link meter + unlock ladder at several levels */}
        <section>
          <h2 className="mb-3 text-[11px] font-medium uppercase tracking-wide text-secondary-400">Link level (1 to 20) & what it unlocks</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {[2, 7, 14, 20].map((lv) => (
              <div key={lv} className="rounded-2xl border border-secondary-200 dark:border-secondary-800 bg-white dark:bg-secondary-900 p-5">
                <LinkMeter level={lv} showLadder />
              </div>
            ))}
          </div>
        </section>

        {/* Casual check-in tone */}
        <section>
          <h2 className="mb-3 text-[11px] font-medium uppercase tracking-wide text-secondary-400">Casual daily check-in — tone by Link level & bond</h2>
          <div className="rounded-2xl border border-secondary-200 dark:border-secondary-800 bg-white dark:bg-secondary-900 p-5 space-y-6 max-w-2xl">
            {SAMPLE_CHECKINS.map((c, i) => <CheckInBubble key={i} {...c} />)}
          </div>
          <p className="mt-3 text-[11px] text-secondary-400 max-w-2xl">
            Affection stays a light touch — productivity, hobbies and friendship first, never sexual or romantic. A strictly-platonic setting removes the warmth flavor entirely.
          </p>
        </section>
      </div>
    </div>
  );
};

export default AgentLinkSpike;
